import {
  ArrowDownIcon,
  ArrowUpIcon,
  Checkbox,
  CheckThinyIcon,
  DotsFourIcon,
  Dropdown,
  DropdownOption,
  EditIcon,
  FontColorEnum,
  FontSizeEnum,
  GoToIcon,
  GroupAddIcon,
  IconButton,
  IconButtonColorEnum,
  IconButtonShapeEnumOld,
  IconButtonSizeEnum,
  IconSizeEnum,
  InformationIcon,
  MenuItemsIcon,
  PauseIsolatedIcon,
  PlayIsolatedIcon,
  PreviewContentIcon,
  SubgroupsIcon,
  Tooltip,
  TooltipPositionsEnum,
  TrashDeleteIcon,
  Typography,
} from '@acceso/design-system';
import React, { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Listbox, { ListboxSelected } from '@/components/Listbox';
import { ThumbnailSizeSlider } from '@/components/ThumbnailSizeSlider/ThumbnailSizeSlider';
import { ToolTipContent } from '@/components/ToolTipContent';
import { BatchDeleteConfirmModal } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/components/broadcastComponents/BatchDeleteConfirmModal';
import { LogoSelectionDropdown } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/components/broadcastComponents/LogoSelectionDropdown';
import { PeopleSelectionDropdown } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/components/broadcastComponents/PeopleSelectionDropdown';
import { useBroadcastScheduleContext } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/context/BroadcastScheduleProvider';
import {
  buildNewPeopleDataToSave,
  buildNewPresenceDataToSave,
  findById,
} from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/helpers/batchSelectionHelpers';
import { GroupedPresencesType } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/hooks/useBatchSelection';
import {
  GROUP_OPTION_VALUES,
  TBroadcastScheduleOnUpdateInline,
  VISUALIZATION_OPTIONS_VALUES,
  VisualizationOption,
} from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/types';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { TClipLogo, TClipPeople } from '@/features/clips/types';
import { useSupportOptions } from '@/features/programs/hooks/useSupportOptions';
import { TSegmentLogo, TSegmentPeople } from '@/features/segments/types';
import { PresenceTypeEnum, TPostPresenceItem, TPresenceItem } from '@/types/presences';

type PresenceTypeConfig = {
  buildDataToSave: (item: TPresenceItem) => TPostPresenceItem;
  entityIdKey: string;
  SelectionDropdown: React.ComponentType<{ onChange: (value: ListboxSelected) => void }>;
};

const PRESENCE_TYPE_CONFIG: Record<PresenceTypeEnum, PresenceTypeConfig> = {
  [PresenceTypeEnum.PEOPLE]: {
    buildDataToSave: item =>
      buildNewPeopleDataToSave(item as TSegmentPeople | TClipPeople) as TPostPresenceItem,
    entityIdKey: 'people_id',
    SelectionDropdown: PeopleSelectionDropdown,
  },
  [PresenceTypeEnum.LOGOS]: {
    buildDataToSave: item =>
      buildNewPresenceDataToSave(item as TSegmentLogo | TClipLogo) as TPostPresenceItem,
    entityIdKey: 'logo_id',
    SelectionDropdown: LogoSelectionDropdown,
  },
};

type GroupingOptionsProps = {
  id: string;
  icon: ReactNode;
  name: string;
  options: DropdownOption[];
  onChange?: (value: string) => void;
  value?: string;
};

type BroadcastActionsBarProps<T extends TPostPresenceItem> = {
  groupedPresences: GroupedPresencesType;
  onUpdateInline?: TBroadcastScheduleOnUpdateInline<T>;
  onDelete?: (item: TPresenceItem, silently?: boolean) => void;
  onChangeCriteria: (criteria: string) => void;
  onChangeSortDirection: (direction: string) => void;
  sortDirection: string;
  onChangeAutoplayClipsMode: () => void;
  groupOptions: DropdownOption[];
  presenceType: PresenceTypeEnum;
};
export function BroadcastActionsBar<T extends TPostPresenceItem>({
  groupedPresences,
  onDelete,
  onUpdateInline,
  onChangeCriteria,
  onChangeSortDirection,
  sortDirection,
  onChangeAutoplayClipsMode,
  groupOptions,
  presenceType,
}: BroadcastActionsBarProps<T>) {
  const { t } = useTranslation();
  const [isBatchMoveDialogOpen, setIsBatchMoveDialogOpen] = useState(false);
  const [isBatchEditSupportDialogOpen, setIsBatchEditSupportDialogOpen] = useState(false);
  const [isBatchDeleteConfirmOpen, setIsBatchDeleteConfirmOpen] = useState(false);
  const {
    selectedPresencesIds,
    setSelectedPresencesIds,
    hasSelectedPresences,
    groupPresenceCriteria,
    isAutoRevealsMode,
    setAutoRevealsMode,
    isAutoplayingClips,
    groupVisualization,
    onChangeVisualization,
    thumbnailSize,
    setThumbnailSize,
  } = useBroadcastScheduleContext();
  const { isGlobalView } = useBroadcastScheduleProgram();
  const { supportOptions } = useSupportOptions();

  const extraControlsByVisualization: Partial<Record<VisualizationOption, ReactNode>> = {
    [VISUALIZATION_OPTIONS_VALUES.Thumbnails]: (
      <ThumbnailSizeSlider value={thumbnailSize} onChange={setThumbnailSize} />
    ),
  };

  const viewOptions: DropdownOption[] = [
    {
      label: t('Lista'),
      value: VISUALIZATION_OPTIONS_VALUES.List,
      icon: <MenuItemsIcon size={IconSizeEnum.XS} />,
      className: '!justify-start',
    },
    {
      label: t('Miniaturas'),
      value: VISUALIZATION_OPTIONS_VALUES.Thumbnails,
      icon: <DotsFourIcon size={IconSizeEnum.XS} />,
      className: '!justify-start',
    },
  ];

  const sortDirectionOptions: DropdownOption[] = [
    {
      label: t('Descendente'),
      value: 'desc',
      icon: <ArrowDownIcon size={IconSizeEnum.XS} />,
      className: '!justify-start',
    },
    {
      label: t('Ascendente'),
      value: 'asc',
      icon: <ArrowUpIcon size={IconSizeEnum.XS} />,
      className: '!justify-start',
    },
  ];

  const getGroupingOptions = (): GroupingOptionsProps[] => {
    const defaultIconOptions = {
      size: IconSizeEnum.MD,
    };
    const sortDirectionLabel = sortDirection === 'asc' ? t('Ascendente') : t('Descendente');
    return [
      {
        id: 'viewMode',
        icon: <PreviewContentIcon {...defaultIconOptions} />,
        name: t(groupVisualization as 'Lista' | 'Miniaturas'),
        options: viewOptions,
        onChange: onChangeVisualization,
        value: groupVisualization,
      },
      {
        id: 'groupMode',
        icon: <SubgroupsIcon {...defaultIconOptions} />,
        name: `${groupPresenceCriteria !== GROUP_OPTION_VALUES.None ? t(groupPresenceCriteria) : t('Agrupar por:')}`,
        options: groupOptions,
      },
      {
        id: 'sortDirection',
        icon:
          sortDirection === 'asc' ? (
            <ArrowUpIcon size={IconSizeEnum.XS} />
          ) : (
            <ArrowDownIcon size={IconSizeEnum.XS} />
          ),
        name: `${sortDirectionLabel}`,
        options: sortDirectionOptions,
        onChange: onChangeSortDirection,
        value: sortDirection,
      },
    ];
  };

  const getGroupingOptionsButtons = () => {
    return (
      <div data-id="filter-presences-bar" className="flex flex-row items-center gap-2">
        {getGroupingOptions().map((item: GroupingOptionsProps, index) => (
          <Dropdown
            className="min-w-[150px]"
            onChange={item.onChange ?? onChangeCriteria}
            key={index}
            textSize={FontSizeEnum.Md}
            options={item.options}
            optionsClassName="!absolute !left-2"
            buttonClassName="bg-transparent hover:bg-transparent text-text-medium-20 hover:text-accent-active-primary-40"
            value={item.value ?? groupPresenceCriteria}
          >
            <div className="flex flex-row items-center gap-1">
              {item.icon}
              {item.name}
            </div>
          </Dropdown>
        ))}
      </div>
    );
  };

  const deletePresences = (presencesToDelete: TPresenceItem[]) => {
    for (let i = 0; i < presencesToDelete.length; i++) {
      const item = presencesToDelete[i];
      setSelectedPresencesIds(prev => prev.filter(id => id !== item.id));
      const silently = i !== presencesToDelete.length - 1;
      onDelete?.(item, silently);
    }
  };

  const batchDeleteAction = () => {
    const presencesToDelete = selectedPresencesIds
      .map(id => findById(groupedPresences, id))
      .filter(Boolean) as TPresenceItem[];

    if (!presencesToDelete.length) return;

    deletePresences(presencesToDelete);
  };

  const batchValidateAction = () => {
    const { buildDataToSave } = PRESENCE_TYPE_CONFIG[presenceType];

    const presencesToValidate = selectedPresencesIds
      .map(id => findById(groupedPresences, id))
      .filter(Boolean) as TPresenceItem[];

    if (!presencesToValidate.length) return;

    for (let i = 0; i < presencesToValidate.length; i++) {
      const item = presencesToValidate[i];
      const silently = i !== presencesToValidate.length - 1;
      const newPresenceData = buildDataToSave(item);

      (newPresenceData as Record<string, unknown>)['review_state'] = 1;
      onUpdateInline?.(newPresenceData as T, silently);
    }
  };

  const batchGroupingAction = () => {
    const presencesToGroup = selectedPresencesIds
      .map(id => findById(groupedPresences, id))
      .filter(Boolean) as TPresenceItem[];

    if (presencesToGroup.length < 2) return;

    const firstPresence = presencesToGroup.reduce((prev, curr) =>
      new Date(prev.start_datetime) <= new Date(curr.start_datetime) ? prev : curr
    );

    const lastPresence = presencesToGroup.reduce((prev, curr) =>
      new Date(prev.end_datetime) >= new Date(curr.end_datetime) ? prev : curr
    );

    const newPresenceData = buildNewPresenceDataToSave(firstPresence as TSegmentLogo | TClipLogo);
    newPresenceData.end_datetime = new Date(lastPresence.end_datetime);
    onUpdateInline?.(newPresenceData as T, true);

    deletePresences(presencesToGroup.filter(p => p.id !== firstPresence.id));
  };

  const batchMoveAction = (presenceSelectedId: string) => {
    const { buildDataToSave, entityIdKey } = PRESENCE_TYPE_CONFIG[presenceType];
    const presencesToMove = selectedPresencesIds
      .map(id => findById(groupedPresences, id))
      .filter(Boolean) as TPresenceItem[];

    if (!presencesToMove.length) return;

    for (let i = 0; i < presencesToMove.length; i++) {
      const item = presencesToMove[i];
      const silently = i !== presencesToMove.length - 1;
      const newPresenceData = buildDataToSave(item);
      (newPresenceData as Record<string, unknown>)[entityIdKey] = presenceSelectedId;
      onUpdateInline?.(newPresenceData as T, silently);
    }
  };

  const batchEditSupportAction = (supportId: string) => {
    const presencesToEdit = selectedPresencesIds
      .map(id => findById(groupedPresences, id))
      .filter(Boolean) as TPresenceItem[];

    if (!presencesToEdit.length) return;

    for (let i = 0; i < presencesToEdit.length; i++) {
      const item = presencesToEdit[i];
      const silently = i !== presencesToEdit.length - 1;
      const newPresenceData = buildNewPresenceDataToSave(item as TSegmentLogo | TClipLogo);
      newPresenceData.parameters.support_id = supportId;

      onUpdateInline?.(newPresenceData as T, silently);
    }
  };

  const canDelete = !!onDelete;
  const canEdit = !!onUpdateInline;

  const getActionButtons = () => {
    if (!canDelete && !canEdit) return null;

    const { SelectionDropdown } = PRESENCE_TYPE_CONFIG[presenceType];
    const defaultButtonOptions = {
      color: IconButtonColorEnum.NONE_PRIMARY,
      shape: IconButtonShapeEnumOld.OLD_SQUARED,
    };
    const defaultIconOptions = {
      size: IconSizeEnum.MD,
    };

    return (
      <div
        data-id="action-logos-bar"
        className="flex flex-row items-center gap-2 border-r-[1px] border-neutral-20 pr-1.5"
      >
        {/* Move presences */}
        {canEdit && (
          <div className="relative">
            <Tooltip help={t('Mover presencias a...')} grow>
              <IconButton
                {...defaultButtonOptions}
                onClick={() => {
                  setIsBatchMoveDialogOpen(!isBatchMoveDialogOpen);
                }}
              >
                <GoToIcon {...defaultIconOptions} />
              </IconButton>
            </Tooltip>
            {isBatchMoveDialogOpen && (
              <div className="absolute right-0 z-10 mt-2 min-w-[300px] border border-neutral-20 bg-white p-4 shadow-lg">
                <SelectionDropdown
                  onChange={value => {
                    const selectedId: string = value[0];
                    selectedId && batchMoveAction(selectedId);
                    setIsBatchMoveDialogOpen(false);
                  }}
                />
              </div>
            )}
          </div>
        )}

        {/* Change support */}
        {canEdit && presenceType === PresenceTypeEnum.LOGOS && (
          <div className="relative">
            <Tooltip help={t('Cambiar el soporte a...')} grow>
              <IconButton
                {...defaultButtonOptions}
                onClick={() => {
                  setIsBatchEditSupportDialogOpen(!isBatchEditSupportDialogOpen);
                }}
              >
                <EditIcon {...defaultIconOptions} />
              </IconButton>
            </Tooltip>
            {isBatchEditSupportDialogOpen && (
              <div className="absolute right-0 z-10 mt-2 min-w-[300px] border border-neutral-20 bg-white p-4 shadow-lg">
                <Listbox
                  dark={false}
                  mode="single"
                  items={supportOptions}
                  onChange={value => {
                    const supportSelected: string = value[0];
                    supportSelected && batchEditSupportAction(supportSelected);
                    setIsBatchEditSupportDialogOpen(false);
                  }}
                  identifiers={{
                    key: 'value',
                    value: 'value',
                    label: 'label',
                  }}
                  selected={undefined}
                />
              </div>
            )}
          </div>
        )}

        {/* Grouping presences */}
        {canEdit && (
          <Tooltip help={t('Agrupar presencias')} grow>
            <IconButton {...defaultButtonOptions} onClick={batchGroupingAction}>
              <GroupAddIcon {...defaultIconOptions} />
            </IconButton>
          </Tooltip>
        )}

        {/* Delete presences */}
        {canDelete && (
          <Tooltip help={t('Eliminar')} grow>
            <IconButton {...defaultButtonOptions} onClick={() => setIsBatchDeleteConfirmOpen(true)}>
              <TrashDeleteIcon {...defaultIconOptions} />
            </IconButton>
          </Tooltip>
        )}

        {/* Validate presences */}
        {canEdit && (
          <Tooltip help={t('Validar')} grow>
            <IconButton {...defaultButtonOptions} onClick={batchValidateAction}>
              <CheckThinyIcon {...defaultIconOptions} />
            </IconButton>
          </Tooltip>
        )}
      </div>
    );
  };

  const getAutoRevealsModeButton = () => {
    if (isGlobalView) return null;
    return (
      <div className="flex select-none flex-row items-center gap-2 border-r-[1px] border-neutral-20 pr-2">
        <Checkbox
          checked={isAutoRevealsMode}
          onChange={() => setAutoRevealsMode(!isAutoRevealsMode)}
          labelClassName="ml-1"
        />
        <Typography color={FontColorEnum.Dark10}>{t('Reproducción continua')}</Typography>
        <div className="flex gap-2">
          <Tooltip
            position={TooltipPositionsEnum.LEFT}
            help={<ToolTipContent text="ReproduccionContinuaTooltip" />}
            grow
          >
            <IconButton size={IconButtonSizeEnum.XSSM} className="!bg-accent-active-primary-30">
              <InformationIcon size={IconSizeEnum.CUSTOM} className="h-3 w-3" />
            </IconButton>
          </Tooltip>
        </div>
      </div>
    );
  };

  const getAutoPlayClipsButton = () => {
    if (isGlobalView) return null;
    return (
      <div className="flex select-none flex-row items-center gap-2">
        <Tooltip
          position={TooltipPositionsEnum.LEFT}
          help={<ToolTipContent text="ReproduccionContinuaClipsTooltip" />}
          grow
        >
          <IconButton
            className="-mr-1"
            size={IconButtonSizeEnum.SM}
            onClick={onChangeAutoplayClipsMode}
          >
            {isAutoplayingClips ? (
              <PauseIsolatedIcon size={IconSizeEnum.CUSTOM} className="h-3 w-3" />
            ) : (
              <PlayIsolatedIcon size={IconSizeEnum.CUSTOM} className="h-3 w-3" />
            )}
          </IconButton>
        </Tooltip>
      </div>
    );
  };

  return (
    <div className="-mt-3 flex flex-row items-center justify-between p-2">
      <div className="flex flex-row items-center gap-2">
        {getGroupingOptionsButtons()}
        {extraControlsByVisualization[groupVisualization]}
      </div>
      <div className="flex flex-row items-center gap-2">
        {hasSelectedPresences && getActionButtons()}
        {getAutoRevealsModeButton()}
        {getAutoPlayClipsButton()}
      </div>
      <BatchDeleteConfirmModal
        isOpen={isBatchDeleteConfirmOpen}
        onClose={() => setIsBatchDeleteConfirmOpen(false)}
        onConfirm={() => {
          batchDeleteAction();
          setIsBatchDeleteConfirmOpen(false);
        }}
      />
    </div>
  );
}
